import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { PhotoUploader, PhotoDisplay } from './media';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  etude: 'À l\'étude',
  offre: 'Offre déposée',
  sous_compromis: 'Sous compromis',
  acte: 'Acte signé',
};

const STATUS_COLORS = {
  etude: 'bg-slate-100 text-slate-700 border-slate-300',
  offre: 'bg-amber-100 text-amber-800 border-amber-300',
  sous_compromis: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  acte: 'bg-foreground text-white border-foreground',
};

const fmt = (n) => (n != null && !isNaN(n) ? new Intl.NumberFormat('de-DE').format(Math.round(n)) : '—');

export function DossiersListPage() {
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    api.get('/dossiers').then(r => setDossiers(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><p>Chargement...</p></div></Layout>;

  return (
    <Layout>
      <div className="space-y-8" data-testid="dossiers-list-page">
        <div className="flex items-end justify-between">
          <div>
            <p className="label-uppercase mb-2">Acquisitions / structuration</p>
            <h1 className="font-heading text-4xl">Dossiers en cours</h1>
          </div>
          {isAdmin && (
            <button onClick={() => navigate('/dossiers/new')} data-testid="add-dossier-btn"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 uppercase tracking-widest text-xs hover:bg-primary/90 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
              Nouveau dossier
            </button>
          )}
        </div>

        {dossiers.length === 0 ? (
          <div className="bg-white border border-border p-10 text-center text-muted-foreground">
            Aucun dossier en cours pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dossiers.map(d => {
              const totalCost = (d.employment_items || []).reduce((s, it) => s + (Number(it.amount) || 0), 0);
              const yieldPct = (d.market_rent && totalCost > 0) ? (d.market_rent / totalCost) * 100 : null;
              return (
                <button key={d.id} type="button" onClick={() => navigate(`/dossiers/${d.id}`)}
                  data-testid={`dossier-card-${d.id}`}
                  className="text-left bg-white border border-border hover:shadow-lg transition-shadow overflow-hidden group flex flex-col">
                  <div className="aspect-[4/3] bg-muted relative overflow-hidden flex-shrink-0">
                    {d.main_photo ? (
                      <PhotoDisplay url={d.main_photo} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-14 h-14 text-muted-foreground/40" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                          <path d="M3 7a2 2 0 012-2h4l2 3h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                    )}
                    <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-wider px-2 py-1 border ${STATUS_COLORS[d.status] || STATUS_COLORS.etude}`}>
                      {STATUS_LABELS[d.status] || d.status}
                    </span>
                  </div>
                  <div className="p-4 space-y-2 flex flex-col flex-1">
                    <h3 className="font-heading text-xl leading-tight">{d.name}</h3>
                    <p className="text-xs text-muted-foreground truncate">{d.address || '—'}</p>
                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border mt-auto">
                      <div>
                        <p className="label-uppercase text-[10px] mb-0.5">Coût de revient</p>
                        <p className="font-heading text-sm">{fmt(totalCost)} €</p>
                      </div>
                      <div>
                        <p className="label-uppercase text-[10px] mb-0.5">Rendement projeté</p>
                        <p className="font-heading text-sm text-gold-dark">{yieldPct != null ? `${yieldPct.toFixed(2)}%` : '—'}</p>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

export function DossierFormPage() {
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    name: '', address: '', main_photo: '', photos: [], videos: [],
    surface: '', status: 'etude', strategy: 'commercial',
    market_rent: '', current_valuation: '', post_renovation_valuation: '',
    employment_items: [{ description: 'Acquisition', amount: '' }],
    notes: '',
  });
  const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/dossiers/${id}`).then(r => {
      const d = r.data;
      setForm({
        name: d.name || '',
        address: d.address || '',
        main_photo: d.main_photo || '',
        photos: d.photos || [],
        videos: d.videos || [],
        surface: d.surface ?? '',
        status: d.status || 'etude',
        strategy: d.strategy || 'commercial',
        market_rent: d.market_rent ?? '',
        current_valuation: d.current_valuation ?? '',
        post_renovation_valuation: d.post_renovation_valuation ?? '',
        employment_items: d.employment_items?.length > 0 ? d.employment_items.map(it => ({ description: it.description, amount: String(it.amount) })) : [{ description: 'Acquisition', amount: '' }],
        notes: d.notes || '',
      });
    }).catch(() => {
      toast.error('Dossier introuvable');
      navigate('/dossiers');
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const totalCost = form.employment_items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const yieldPct = (Number(form.market_rent) > 0 && totalCost > 0) ? (Number(form.market_rent) / totalCost) * 100 : null;

  const updateItem = (idx, field, value) => {
    setForm(p => ({
      ...p,
      employment_items: p.employment_items.map((it, i) => i === idx ? { ...it, [field]: value } : it),
    }));
  };
  const addItem = () => setForm(p => ({ ...p, employment_items: [...p.employment_items, { description: '', amount: '' }] }));
  const removeItem = (idx) => setForm(p => ({ ...p, employment_items: p.employment_items.filter((_, i) => i !== idx) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      address: form.address,
      main_photo: form.main_photo,
      photos: form.photos,
      videos: form.videos,
      surface: Number(form.surface) || 0,
      status: form.status,
      strategy: form.strategy,
      market_rent: Number(form.market_rent) || 0,
      current_valuation: Number(form.current_valuation) || 0,
      post_renovation_valuation: Number(form.post_renovation_valuation) || 0,
      employment_items: form.employment_items
        .filter(it => it.description || it.amount)
        .map(it => ({ description: it.description || '', amount: Number(it.amount) || 0 })),
      notes: form.notes,
    };
    try {
      if (isEdit) {
        await api.put(`/dossiers/${id}`, payload);
        toast.success('Dossier modifié');
        navigate(`/dossiers/${id}`);
      } else {
        const res = await api.post('/dossiers', payload);
        toast.success('Dossier créé');
        navigate(`/dossiers/${res.data.id}`);
      }
    } catch (err) { toast.error('Erreur lors de la sauvegarde'); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><p>Chargement...</p></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl space-y-8" data-testid="dossier-form-page">
        <div>
          <p className="label-uppercase mb-2">Acquisitions / structuration</p>
          <h1 className="font-heading text-3xl">{isEdit ? 'Modifier le dossier' : 'Nouveau dossier'}</h1>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Identification */}
          <div className="bg-white border border-border p-6 space-y-4">
            <h2 className="font-heading text-lg mb-2">Identification</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label-uppercase block mb-1">Nom du dossier *</label>
                <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required
                  data-testid="dossier-input-name"
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label className="label-uppercase block mb-1">Statut</label>
                <select value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))}
                  data-testid="dossier-select-status"
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none">
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="label-uppercase block mb-1">Stratégie</label>
                <select value={form.strategy} onChange={e => setForm(p => ({...p, strategy: e.target.value}))}
                  data-testid="dossier-select-strategy"
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none">
                  <option value="commercial">Commercial</option>
                  <option value="marchand_de_bien">Marchand de bien</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-uppercase block mb-1">Adresse</label>
                <input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))}
                  data-testid="dossier-input-address"
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label className="label-uppercase block mb-1">Surface (m²)</label>
                <input type="number" step="0.01" value={form.surface} onChange={e => setForm(p => ({...p, surface: e.target.value}))}
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white border border-border p-6 space-y-4">
            <h2 className="font-heading text-lg">Médias</h2>
            <PhotoUploader label="Photo principale" value={form.main_photo ? [form.main_photo] : []}
              onChange={(url) => setForm(p => ({...p, main_photo: url || ''}))} multiple={false} token={token} />
            <PhotoUploader label="Galerie photos" value={form.photos}
              onChange={(urls) => setForm(p => ({...p, photos: urls}))} multiple token={token} />
          </div>

          {/* Données financières projetées */}
          <div className="bg-white border border-border p-6 space-y-4">
            <h2 className="font-heading text-lg mb-2">Données financières projetées</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="label-uppercase block mb-1">Loyer de marché (€/an)</label>
                <input type="number" step="0.01" value={form.market_rent} onChange={e => setForm(p => ({...p, market_rent: e.target.value}))}
                  data-testid="dossier-input-market-rent"
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label className="label-uppercase block mb-1">Valorisation actuelle (€)</label>
                <input type="number" step="0.01" value={form.current_valuation} onChange={e => setForm(p => ({...p, current_valuation: e.target.value}))}
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label className="label-uppercase block mb-1">Valorisation après relocation (€)</label>
                <input type="number" step="0.01" value={form.post_renovation_valuation} onChange={e => setForm(p => ({...p, post_renovation_valuation: e.target.value}))}
                  className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" />
              </div>
            </div>
          </div>

          {/* Tableau emploi des fonds */}
          <div className="bg-white border border-border p-6 space-y-4">
            <h2 className="font-heading text-lg">Tableau d'emploi des fonds</h2>
            <table className="w-full text-sm" data-testid="dossier-employment-table">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2 label-uppercase text-xs font-semibold">Descriptif</th>
                  <th className="text-right p-2 label-uppercase text-xs font-semibold w-40">Montant (€)</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {form.employment_items.map((it, idx) => (
                  <tr key={idx} className="border-b border-border">
                    <td className="p-2">
                      <input value={it.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                        placeholder="Ex: Acquisition, Frais de notaire, Travaux..."
                        data-testid={`dossier-item-desc-${idx}`}
                        className="w-full border border-transparent hover:border-input focus:border-primary focus:outline-none px-2 py-1" />
                    </td>
                    <td className="p-2">
                      <input type="number" step="0.01" value={it.amount} onChange={e => updateItem(idx, 'amount', e.target.value)}
                        placeholder="0"
                        data-testid={`dossier-item-amount-${idx}`}
                        className="w-full border border-transparent hover:border-input focus:border-primary focus:outline-none px-2 py-1 text-right" />
                    </td>
                    <td className="p-2 text-center">
                      {form.employment_items.length > 1 && (
                        <button type="button" onClick={() => removeItem(idx)} title="Supprimer cette ligne"
                          className="text-muted-foreground hover:text-destructive">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-foreground text-white">
                  <td className="p-3 font-heading uppercase tracking-wider text-xs">Total coût de revient</td>
                  <td className="p-3 text-right font-heading text-lg" data-testid="dossier-total-cost">{fmt(totalCost)} €</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            <button type="button" onClick={addItem} data-testid="dossier-add-item-btn"
              className="inline-flex items-center gap-2 text-sm border border-border px-4 py-2 hover:bg-muted">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
              Ajouter une ligne
            </button>
          </div>

          {/* Rendement calculé */}
          <div className="bg-gradient-to-br from-gold/10 to-gold/5 border border-gold/30 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-uppercase mb-1">Rendement projeté</p>
                <p className="text-xs text-muted-foreground">Loyer de marché ÷ Coût de revient total</p>
              </div>
              <p className="font-heading text-4xl text-gold-dark" data-testid="dossier-projected-yield">
                {yieldPct != null ? `${yieldPct.toFixed(2)}%` : '—'}
              </p>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-border p-6 space-y-2">
            <label className="label-uppercase block">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))}
              rows={4} className="w-full border border-input px-3 py-2 focus:border-primary focus:outline-none" />
          </div>

          <div className="flex gap-3 sticky bottom-4">
            <button type="submit" data-testid="dossier-submit-btn"
              className="bg-primary text-primary-foreground px-8 py-3 uppercase tracking-widest text-xs hover:bg-primary/90 transition-colors">
              {isEdit ? 'Enregistrer' : 'Créer le dossier'}
            </button>
            <button type="button" onClick={() => navigate('/dossiers')}
              className="border border-border px-8 py-3 text-sm hover:bg-muted">Annuler</button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export function DossierDetailPage() {
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    api.get(`/dossiers/${id}`).then(r => setDossier(r.data)).catch(() => {
      toast.error('Dossier introuvable');
      navigate('/dossiers');
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    try {
      await api.delete(`/dossiers/${id}`);
      toast.success('Dossier supprimé');
      navigate('/dossiers');
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading || !dossier) return <Layout><div className="flex items-center justify-center h-64"><p>Chargement...</p></div></Layout>;

  const totalCost = (dossier.employment_items || []).reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const yieldPct = (dossier.market_rent && totalCost > 0) ? (dossier.market_rent / totalCost) * 100 : null;

  return (
    <Layout>
      <div className="space-y-8" data-testid="dossier-detail-page">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <button onClick={() => navigate('/dossiers')} className="text-xs text-muted-foreground hover:text-foreground uppercase tracking-wider mb-2">← Retour aux dossiers</button>
            <h1 className="font-heading text-4xl">{dossier.name}</h1>
            <p className="text-muted-foreground mt-1">{dossier.address || '—'}</p>
            <span className={`inline-block mt-3 text-[10px] uppercase tracking-wider px-2 py-1 border ${STATUS_COLORS[dossier.status] || STATUS_COLORS.etude}`}>
              {STATUS_LABELS[dossier.status] || dossier.status}
            </span>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <button onClick={() => navigate(`/dossiers/${id}/edit`)} className="border border-border px-4 py-2 text-sm hover:bg-muted">Modifier</button>
              <button onClick={() => setConfirmDelete(true)} className="border border-destructive/40 text-destructive px-4 py-2 text-sm hover:bg-destructive/10">Supprimer</button>
            </div>
          )}
        </div>

        {dossier.main_photo && (
          <div className="bg-muted aspect-[4/3] max-h-[420px] overflow-hidden">
            <PhotoDisplay url={dossier.main_photo} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white border border-border p-5">
            <p className="label-uppercase text-xs mb-1">Loyer de marché</p>
            <p className="font-heading text-2xl">{fmt(dossier.market_rent)} €<span className="text-sm text-muted-foreground"> /an</span></p>
          </div>
          <div className="bg-white border border-border p-5">
            <p className="label-uppercase text-xs mb-1">Valorisation actuelle</p>
            <p className="font-heading text-2xl">{fmt(dossier.current_valuation)} €</p>
          </div>
          <div className="bg-white border border-border p-5">
            <p className="label-uppercase text-xs mb-1">Val. après relocation</p>
            <p className="font-heading text-2xl">{fmt(dossier.post_renovation_valuation)} €</p>
          </div>
        </div>

        <div className="bg-white border border-border">
          <div className="p-6 border-b border-border">
            <h2 className="font-heading text-2xl">Emploi des fonds</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr><th className="text-left p-3 label-uppercase text-xs">Descriptif</th><th className="text-right p-3 label-uppercase text-xs w-40">Montant</th></tr>
            </thead>
            <tbody>
              {(dossier.employment_items || []).map((it, i) => (
                <tr key={i} className="border-b border-border">
                  <td className="p-3">{it.description}</td>
                  <td className="p-3 text-right font-heading">{fmt(it.amount)} €</td>
                </tr>
              ))}
              {(!dossier.employment_items || dossier.employment_items.length === 0) && (
                <tr><td colSpan={2} className="p-6 text-center text-muted-foreground italic">Aucun emploi renseigné</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-foreground text-white">
                <td className="p-3 font-heading uppercase tracking-wider text-xs">Total coût de revient</td>
                <td className="p-3 text-right font-heading text-lg">{fmt(totalCost)} €</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="bg-gradient-to-br from-gold/10 to-gold/5 border border-gold/30 p-6 flex items-center justify-between">
          <div>
            <p className="label-uppercase mb-1">Rendement projeté</p>
            <p className="text-xs text-muted-foreground">Loyer de marché ÷ Coût de revient total</p>
          </div>
          <p className="font-heading text-4xl text-gold-dark">{yieldPct != null ? `${yieldPct.toFixed(2)}%` : '—'}</p>
        </div>

        {dossier.notes && (
          <div className="bg-white border border-border p-6">
            <p className="label-uppercase text-xs mb-2">Notes</p>
            <p className="text-sm whitespace-pre-line">{dossier.notes}</p>
          </div>
        )}

        {confirmDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDelete(false)}>
            <div className="bg-white p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-testid="confirm-delete-dossier-modal">
              <h2 className="font-heading text-xl mb-3">Supprimer ce dossier ?</h2>
              <p className="text-sm text-muted-foreground mb-4">Cette action est irréversible.</p>
              <div className="flex gap-3">
                <button onClick={handleDelete}
                  className="flex-1 bg-destructive text-white px-4 py-2.5 text-sm uppercase tracking-wider hover:bg-destructive/90">Supprimer</button>
                <button onClick={() => setConfirmDelete(false)}
                  className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-muted">Annuler</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
